import {
  EquippedItem,
  LegacyPledgeTier,
  SubscriptionTier,
} from '../../types/custom'
import { toast } from 'sonner'
import axios, { AxiosError } from 'axios'
import { TFunction } from 'i18next'
import { getPendingJobById } from '../api/job'
import { deburr } from 'lodash'

export const imageUrl = (path: string) => {
  const assetHost = window.page_info.assetHost
  return `${assetHost}/images/${path}`
}

export const wowheadUrl = (
  gameVersion: wowaudit.PageInfo['gameVersion'],
  item?: EquippedItem,
  id?: number,
) => {
  const gameVersionMap = {
    live: '',
    classic_era: 'classic/',
    classic_anniversary: 'tbc/',
    classic_progression: 'mop-classic/',
  }

  if (item) {
    return `https://wowhead.com/${gameVersionMap[gameVersion]}item=${
      item.id
    }&bonus=${(item.bonus_ids ?? []).join(':')}&gems=${(item.sockets ?? [])
      .map((socket) => socket.gem)
      .join(':')}&ench=${item.enchant?.id}`
  } else if (id) {
    return `https://wowhead.com/${gameVersionMap[gameVersion]}item=${id}`
  }
}

export const mediaUrl = (
  character: wowaudit.Character | wowaudit.CharacterReference | undefined,
  type: 'avatar' | 'main-raw',
) => {
  const extensionByType = {
    avatar: 'jpg',
    'main-raw': 'png',
  }

  if (character && character.mediaZone) {
    return `https://render.worldofwarcraft.com/${
      character.realm.namespace
    }/character/${deburr(character.realm.slug)}/${character.mediaZone}/${
      character.profileId.split('-')[1]
    }-${type}.${extensionByType[type]}`
  } else if (type === 'avatar') {
    return imageUrl('bnet-temp.png')
  } else if (type === 'main-raw') {
    return imageUrl('avatar-fallback.png')
  }
}

type CustomAxiosError = AxiosError<{
  error: string
  description: string
  message: string
  success: boolean
  backtrace?: string
}>

export const getErrorDetails = (
  error: Error | CustomAxiosError | string,
  t: TFunction,
) => {
  if (typeof error === 'string') {
    return { title: t('errors.custom'), description: t(error) }
  } else if (axios.isAxiosError(error) && error.response) {
    const description =
      (error as CustomAxiosError).response?.data.description ?? 'unknown error'
    const isCustomString = t(description) === description

    return {
      title: t(`errors.${(error as CustomAxiosError).response?.data.error}`),
      description: isCustomString ? description : t(description),
    }
  } else {
    return {
      title: t('errors.browser_error'),
      description: t('errors.fallback_description'),
    }
  }
}

export const displayErrorToast = (
  error: Error | AxiosError | string,
  t: TFunction,
) => {
  const textToDisplay = getErrorDetails(error, t)
  return toast.error(textToDisplay.title, {
    description: textToDisplay.description,
  })
}

export const awaitPendingJob = <T>(
  jobId: string,
  callback: (result: wowaudit.JobStatus<T>) => void,
) => {
  setTimeout(async () => {
    const pendingJob = await getPendingJobById<T>(jobId)
    console.log(pendingJob)

    if (['queued', 'working'].includes(pendingJob.status)) {
      awaitPendingJob(jobId, callback)
    } else {
      if (
        ['failed', 'interrupted'].includes(pendingJob.status) &&
        !pendingJob.error
      ) {
        pendingJob.error = `Your request ${pendingJob.status} for an unknown reason. Please try again.`
      }

      if (pendingJob.content) {
        pendingJob.content = JSON.parse(pendingJob.content as string)
      }

      callback(pendingJob)
    }
  }, 1000)
}

export const awaitPendingJobs = <T>(jobIds: string[]) =>
  Promise.all(
    jobIds.map(
      (jobId) =>
        new Promise<wowaudit.JobStatus<T>>((resolve) =>
          awaitPendingJob<T>(jobId, resolve),
        ),
    ),
  )

// We have to hardcode the class names, Tailwind doesn't recognise
// class names that are composed dynamically at runtime
export const premiumColors: {
  [key in LegacyPledgeTier | SubscriptionTier]: { text: string; border: string }
} = {
  Free: { text: 'opacity-70 italic', border: 'border-border' },
  Bronze: {
    text: 'text-subscription-bronze',
    border: 'border-subscription-bronze',
  },
  Silver: {
    text: 'text-subscription-silver',
    border: 'border-subscription-silver',
  },
  Gold: { text: 'text-subscription-gold', border: 'border-subscription-gold' },
  Platinum: {
    text: 'text-subscription-platinum',
    border: 'border-subscription-platinum',
  },
  Rare: { text: 'text-quality-rare', border: 'border-quality-rare' },
  Epic: { text: 'text-quality-epic', border: 'border-quality-epic' },
  Legendary: {
    text: 'text-quality-legendary',
    border: 'border-quality-legendary',
  },
}

export const premiumColorForGuild = (guild: wowaudit.Guild) => {
  if (guild.premiumPoints >= 30)
    return { className: 'text-quality-legendary', tooltip: 'Legendary tier' }
  if (guild.premiumPoints >= 15)
    return { className: 'text-quality-epic', tooltip: 'Epic tier' }
  if (guild.premiumPoints >= 5)
    return { className: 'text-quality-rare', tooltip: 'Rare tier' }
  if (guild.premiumPoints > 0)
    return { className: 'text-accent-foreground', tooltip: 'Uncommon tier' }
  return null
}

// We need to encode the path to avoid issues with non-ASCII characters (e.g. cyrillic guild names)
export const encodePath = (path: string) => {
  return path
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/')
}
